import {Injectable, inject} from '@angular/core';
import {Router} from '@angular/router';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {fromEvent, merge, Subscription, timer} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {InactivityModalComponent} from '../Components/common/inactivity-modal/inactivity-modal.component';
import {AppConfigService} from './app-config.service';

@Injectable({
  providedIn: 'root'
})
export class InactivityService {

  private activitySubscription?: Subscription;
  private modalRef?: NgbModalRef;

  constructor(
    private modalService: NgbModal,
    private authService: AuthService,
    private router: Router,
    private appConfig: AppConfigService
  ) {
  }

  private get timeoutSeconds() {
    return this.appConfig.userIdleTimeOut || 300;
  }

  private get countdownSeconds() {
    return this.appConfig.userIdleCountdownLength || 30;
  }

  startTracking(): void {
    if (!this.appConfig.userIdleTimeOutOn) {
      return;
    }

    if (this.activitySubscription) {
      return;
    }

    const activityEvents = merge(
      fromEvent(window, 'mousemove'),
      fromEvent(window, 'click'),
      fromEvent(window, 'keypress'),
      fromEvent(window, 'scroll'),
      fromEvent(window, 'touchstart'),
      timer(this.timeoutSeconds * 1000) // Initial timer
    );

    this.activitySubscription = activityEvents.pipe(
      switchMap(() => timer(this.timeoutSeconds * 1000))
    ).subscribe(() => {
      this.onTimeout();
    });
  }

  stopTracking(): void {
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
      this.activitySubscription = undefined;
    }
  }

  private resetTimer(): void {
    this.stopTracking();
    this.startTracking();
  }

  private onTimeout(): void {
    // Only show modal if user is logged in
    if (!this.authService.currentUser()) {
      // Re-start tracking to check again later
      this.resetTimer();
      return;
    }

    // Don't show modal if already on login page
    if (this.router.url.includes('login')) {
      this.resetTimer();
      return;
    }

    // Prevent multiple modals
    if (this.modalRef) {
      return;
    }

    this.modalRef = this.modalService.open(InactivityModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true
    });

    this.modalRef.componentInstance.countdown = this.countdownSeconds;

    this.modalRef.result.then(
      (result) => {
        this.modalRef = undefined;
        if (result === 'stay') {
          this.authService.markSessionActivity();
          this.resetTimer();
        } else if (result === 'logout') {
          this.logout();
        }
      },
      () => {
        this.modalRef = undefined;
        // If dismissed (shouldn't happen with static backdrop), reset timer
        this.resetTimer();
      }
    );
  }

  private logout(): void {
    this.authService.logout(true, 'Your session has timed out due to inactivity.');
    this.stopTracking();
  }
}
